import { Check, ChevronDown } from 'lucide-react';
import { Select as RadixSelect } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { useFieldControl } from './Field';
import { controlClasses, controlSizes } from './Input';

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Np. kropka koloru klienta. */
  leading?: ReactNode;
  disabled?: boolean;
}

/** Lista wyboru (Radix Select): klawiatura, czytniki ekranu, wygląd jak Input. */
export function Select<V extends string = string>({
  value,
  onValueChange,
  options,
  placeholder = 'Wybierz…',
  size = 'm',
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
}: {
  value: V | undefined;
  onValueChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: string;
  size?: keyof typeof controlSizes;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const field = useFieldControl({ id });
  return (
    <RadixSelect.Root value={value} onValueChange={(v) => onValueChange(v as V)} disabled={disabled}>
      <RadixSelect.Trigger
        id={field.id}
        aria-label={ariaLabel}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        className={cn(
          controlClasses,
          controlSizes[size],
          'inline-flex items-center justify-between gap-2 text-left data-[placeholder]:text-fg-muted',
          className,
        )}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-50 max-h-[min(var(--radix-select-content-available-height),320px)] min-w-[var(--radix-select-trigger-width)]',
            'overflow-hidden rounded-lg border border-line bg-canvas p-1 shadow-popover animate-pop-in',
          )}
        >
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex h-8 cursor-pointer items-center gap-2 rounded-md pr-8 pl-2.5 text-body-s text-fg outline-none select-none',
                  'data-[highlighted]:bg-elevated data-[disabled]:opacity-50',
                )}
              >
                {option.leading}
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="absolute right-2.5">
                  <Check size={14} strokeWidth={2} aria-hidden className="text-accent" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
